import { useEffect, useState, useCallback } from 'react'
import { useWarnAboutChange } from '@refinedev/core'

/**
 * Blocks browser back/forward button navigation when there are unsaved changes.
 *
 * Refine's UnsavedChangesNotifier only intercepts programmatic navigation
 * (e.g. clicking in-app links). The browser's back/forward buttons fire
 * `popstate` directly, bypassing React Router's navigator patches.
 *
 * CRITICAL: The popstate listener is registered at MODULE level (import time)
 * so it fires BEFORE React Router's listener. React Router registers its
 * popstate listener during render (in createBrowserHistory), which runs before
 * useEffect. If we registered in useEffect, React Router would process the
 * navigation first, unmount the edit component, and lose all form state.
 *
 * A non-blocking React dialog is used instead of window.confirm() because
 * the native confirm dialog triggers a browser focus event on close, which
 * causes React Query's refetchOnWindowFocus to refetch form data and
 * @refinedev/react-hook-form to reset() the form, wiping edits.
 *
 * Flow:
 * - popstate fires → module listener intercepts before React Router
 * - stopImmediatePropagation prevents React Router from processing
 * - URL is immediately reverted with history.go(1)
 * - A React dialog asks the user to confirm
 * - On Cancel: dialog closes, component was never unmounted, state intact
 * - On Leave: history.go(-1) with isAllowing flag lets React Router process
 */

// ── Module-level state ──────────────────────────────────────────────────────
// These live outside React so the popstate listener (also outside React)
// can read them synchronously.
let _warnWhen = false
let _showDialog: ((show: boolean) => void) | null = null
const _isReverting = { current: false }
const _isAllowing = { current: false }

// ── Module-level popstate listener ──────────────────────────────────────────
// Registered at import time, BEFORE React Router mounts.
window.addEventListener(
  'popstate',
  (e: PopStateEvent) => {
    // Skip the popstate fired by our own history.go(1) revert
    if (_isReverting.current) {
      _isReverting.current = false
      e.stopImmediatePropagation()
      return
    }

    // Let React Router handle the popstate after user confirms leaving
    if (_isAllowing.current) {
      _isAllowing.current = false
      return
    }

    if (!_warnWhen) return

    // Block React Router from seeing this navigation
    e.stopImmediatePropagation()

    // Immediately revert the URL so React Router never processes the change
    _isReverting.current = true
    window.history.go(1)

    // Show a non-blocking React dialog
    _showDialog?.(true)
  },
  true, // capture phase
)

// ── React hook ──────────────────────────────────────────────────────────────
export function useBlockBrowserNavigation() {
  const { warnWhen, setWarnWhen } = useWarnAboutChange()
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)

  // Bridge React state → module-level variable
  useEffect(() => {
    _warnWhen = warnWhen ?? false
  }, [warnWhen])

  // Bridge React setState → module-level reference
  useEffect(() => {
    _showDialog = setShowConfirmDialog
    return () => {
      _showDialog = null
    }
  }, [])

  const handleConfirmLeave = useCallback(() => {
    setShowConfirmDialog(false)
    setWarnWhen?.(false)
    _warnWhen = false
    _isAllowing.current = true
    window.history.go(-1)
  }, [setWarnWhen])

  const handleCancelLeave = useCallback(() => {
    setShowConfirmDialog(false)
  }, [])

  return { showConfirmDialog, handleConfirmLeave, handleCancelLeave }
}
